import { useState } from 'react';

import { Card } from '@components/card';
import { FormLabelContainer } from '@components/form-label/form-label';
import { IconButton } from '@components/icon-button';
import { IconDocument } from '@components/icons/document';
import { IconDownload } from '@components/icons/download';
import { IconReplace } from '@components/icons/replace';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';
import { fileNameFromPath } from '@utils/strings';

import { FileDropzone, FileDropzoneProps } from './file-dropzone';

export type GenericFileDropzoneInputProps = FileDropzoneProps & {
  // defaultPath instead of defaultValue because form fields need defaultValue to match the onChange type
  defaultPath?: string;
};

export const GenericFileDropzoneInput = ({
  defaultPath,
  onChange,
  ...rest
}: GenericFileDropzoneInputProps) => {
  const [filename, setFilename] = useState<string>(
    defaultPath ? fileNameFromPath(defaultPath) : '',
  );

  const handleChange = (file: File) => {
    setFilename(file.name);
    onChange?.(file);
  };

  const handleDelete = () => {
    setFilename('');
    onChange?.(null);
  };

  return filename ? (
    <FileLoaded filename={filename} {...rest} onDelete={handleDelete} defaultPath={defaultPath} />
  ) : (
    <FileDropzone onChange={handleChange} {...rest} />
  );
};

type FileLoadedProps = Pick<GenericFileDropzoneInputProps, 'defaultPath'> & {
  filename: string;
  onDelete(): void;
};

const FileLoaded = ({ filename, onDelete, defaultPath, ...labelProps }: FileLoadedProps) => {
  return (
    <FormLabelContainer {...labelProps}>
      <Card spaceX={1} spaceY={1}>
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center" style={{ overflow: 'hidden' }}>
            <IconDocument color="blue100" style={{ marginLeft: pxToRem(8) }} />
            <Text font="regular14" text={filename} ellipsis />
          </Stack>
          <Stack orientation="horizontal">
            {defaultPath && (
              <IconButton
                icon={<IconDownload />}
                variant="ghost-purple"
                href={defaultPath}
                data-testid="download-button"
              />
            )}
            <IconButton
              icon={<IconReplace />}
              variant="ghost-purple"
              onClick={onDelete}
              data-testid="replace-button"
            />
          </Stack>
        </Stack>
      </Card>
    </FormLabelContainer>
  );
};
